import React, { useState } from 'react';
import { Col, Row, Radio, Checkbox, Space, RadioChangeEvent } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import LocationSelector, { AddressType } from './LocationSelector';

interface RouteSelectionCardProps {
  locations: { key: string; name: string }[];
  onStartChange?: (value: string | AddressType | null) => void;
  onEndChange?: (value: string | AddressType | null) => void;
  onSingleChange?: (value: string | AddressType | null) => void;
  onRouteTypeChange?: (routeType: 'single' | 'multi') => void;
}

const RouteSelectionCard: React.FC<RouteSelectionCardProps> = ({
  locations,
  onStartChange,
  onEndChange,
  onSingleChange,
  onRouteTypeChange,
}) => {
  const [routeType, setRouteType] = useState<'single' | 'multi'>('single');
  const [sameStartEnd, setSameStartEnd] = useState(false);
  const [startLocation, setStartLocation] = useState<string | AddressType | null>(null);
  const [endLocation, setEndLocation] = useState<string | AddressType | null>(null);
  const [singleLocation, setSingleLocation] = useState<string | AddressType | null>(null);

  const RequiredStar = () => <span className="c-red">*</span>;

  const handleRouteTypeChange = (e: RadioChangeEvent) => {
    const value = e.target.value as 'single' | 'multi';
    setRouteType(value);
    setSameStartEnd(false);
    setSingleLocation(null);
    setStartLocation(null);
    setEndLocation(null);

    onRouteTypeChange?.(value);

    if (value === 'multi') {
      setSameStartEnd(true);
    } else {
      setSameStartEnd(false);
    }
  };

  const onSameStartEndChange = (e: CheckboxChangeEvent) => {
    setSameStartEnd(e.target.checked);
    if (e.target.checked) {
      setSingleLocation(null);
      setStartLocation(null);
      setEndLocation(null);
      onStartChange?.(null);
      onEndChange?.(null);
    } else {
      setSingleLocation(null);
      setStartLocation(null);
      setEndLocation(null);
      onSingleChange?.(null);
      onStartChange?.(null);
      onEndChange?.(null);
    }
  };

  const renderRouteSelection = () => {
    if (routeType === 'multi') {
      return (
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={16}>
            <LocationSelector
              label={
                <>
                  Start &amp; End Location <RequiredStar />
                </>
              }
              value={startLocation}
              locations={locations}
              onChange={(val) => {
                setStartLocation(val);
                setEndLocation(val);
                onStartChange?.(val);
                onEndChange?.(val);
              }}
            />
          </Col>
          <Col xs={24} md={8} className="d-flex ai-center h-100">
            <Checkbox checked className="ws-nowrap">
              Start and End at the Same Location
            </Checkbox>
          </Col>
        </Row>
      );
    }

    if (sameStartEnd) {
      return (
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={16}>
            <LocationSelector
              label={
                <>
                  Start &amp; End Location <RequiredStar />
                </>
              }
              value={singleLocation}
              locations={locations}
              onChange={(val) => {
                setSingleLocation(val);
                onSingleChange?.(val);
                onStartChange?.(val);
                onEndChange?.(val);
              }}
            />
          </Col>
          <Col xs={24} md={8} className="d-flex ai-center h-100">
            <Checkbox checked={sameStartEnd} onChange={onSameStartEndChange} className="ws-nowrap">
              Start and End at the Same Location
            </Checkbox>
          </Col>
        </Row>
      );
    }

    return (
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} md={8}>
          <LocationSelector
            label={
              <>
                Start Location <RequiredStar />
              </>
            }
            value={startLocation}
            locations={locations}
            onChange={(val) => {
              setStartLocation(val);
              onStartChange?.(val);
            }}
          />
        </Col>
        <Col xs={24} md={8}>
          <LocationSelector
            label={
              <>
                End Location <RequiredStar />
              </>
            }
            value={endLocation}
            locations={locations}
            onChange={(val) => {
              setEndLocation(val);
              onEndChange?.(val);
            }}
          />
        </Col>
        <Col xs={24} md={8} className="d-flex ai-center h-100">
          <Checkbox checked={sameStartEnd} onChange={onSameStartEndChange} className="ws-nowrap">
            Start and End at the Same Location
          </Checkbox>
        </Col>
      </Row>
    );
  };

  return (
    <div>
      <Row gutter={[16, 12]} align="middle" className="mg-b-20">
        <Col xs={24} md={12}>
          <Space align="center" className="w-100">
            <span className="fw-600 min-w-90">
              Route Type <RequiredStar /> :{' '}
            </span>
            <Radio.Group onChange={handleRouteTypeChange} value={routeType} className="w-auto">
              <Space size="large">
                <Radio value="single">Single Route</Radio>
                <Radio value="multi">Multi Route</Radio>
              </Space>
            </Radio.Group>
          </Space>
        </Col>
      </Row>
      {renderRouteSelection()}
    </div>
  );
};

const noop = () => undefined;

RouteSelectionCard.defaultProps = {
  onStartChange: noop,
  onEndChange: noop,
  onSingleChange: noop,
  onRouteTypeChange: noop,
};

export default RouteSelectionCard;
